import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Bot, CalendarClock, ChevronRight } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { CoverImage } from "@/components/CoverImage";
import { t, fmtDate } from "@/i18n";
import { Spotlight, stagger, fadeUp } from "./motion";

const daysUntil = (iso) => Math.max(0, Math.ceil((new Date(iso + "T00:00:00") - new Date()) / 86400000));

export const BotPanel = ({ jobs }) => (
  <Spotlight className="p-6" data-testid="bot-panel">
    <div className="flex items-center justify-between mb-3">
      <h2 className="font-display text-lg font-semibold flex items-center gap-2"><Bot className="h-4 w-4 text-primary" /> {t("Bot Downtown")}</h2>
      {jobs.length > 0 && <span className="text-[10px] font-semibold uppercase tracking-wider text-primary flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-primary pulse-dot" /> {t("en vivo")}</span>}
    </div>
    {jobs.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="no-active-jobs">{t("Sin ejecuciones activas.")}</p> : (
      <motion.div variants={stagger} initial="hidden" animate="show">
        {jobs.map((j) => (
          <motion.div key={j.id} variants={fadeUp}>
            <Link to={`/bot/${j.id}`} className="block rounded-lg border border-border p-3 mb-2 hover:border-primary/60 hover:bg-primary/5 transition-colors relative overflow-hidden" data-testid={`active-job-${j.id}`}>
              {j.status === "running" && <span className="absolute inset-x-0 bottom-0 h-0.5 bg-primary/70 shimmer-bar" />}
              <p className="text-sm font-medium truncate">{j.release_title}</p>
              <div className="flex items-center justify-between mt-1"><span className="text-xs text-muted-foreground truncate">{j.current_step || t("en cola")}</span><StatusBadge status={j.status} testId="job-status-badge" /></div>
            </Link>
          </motion.div>
        ))}
      </motion.div>
    )}
    <Link to="/bot" className="text-sm text-primary hover:underline mt-2 inline-flex items-center gap-1 link-arrow" data-testid="link-bot-page">{t("Abrir consola del bot")} <ChevronRight className="h-3.5 w-3.5" /></Link>
  </Spotlight>
);

export const UpcomingPanel = ({ upcoming }) => (
  <Spotlight className="p-6" data-testid="upcoming-panel">
    <h2 className="font-display text-lg font-semibold mb-3 flex items-center gap-2"><CalendarClock className="h-4 w-4 text-primary" /> {t("Próximos lanzamientos")}</h2>
    {!upcoming?.length ? <p className="text-sm text-muted-foreground" data-testid="no-upcoming">{t("Nada programado.")}</p> : (
      <motion.ul variants={stagger} initial="hidden" animate="show" className="space-y-2">
        {upcoming.map((u) => {
          const d = daysUntil(u.live_date);
          return (
            <motion.li key={u.id} variants={fadeUp} whileHover={{ x: 3 }}>
              <Link to={`/lanzamientos/${u.id}`} className="flex items-center gap-3 rounded-lg -mx-2 px-2 py-2 hover:bg-muted/40 transition-colors" data-testid={`upcoming-${u.id}`}>
                <CoverImage fileId={u.cover_file_id} className="h-9 w-9 shrink-0" />
                <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{u.title}</p><p className="text-xs text-muted-foreground truncate">{u.artist_name} · {fmtDate(u.live_date, { day: "numeric", month: "short" })}</p></div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums ${d <= 3 ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                  {d === 0 ? t("hoy") : t("{n} d", { n: d })}
                </span>
              </Link>
            </motion.li>
          );
        })}
      </motion.ul>
    )}
  </Spotlight>
);
